import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Card,
    Form,
    OverlayTrigger,
    Popover,
    Toast,
    ToastContainer,
} from "react-bootstrap";
import { Map as MapLibreMap, type MapRef, NavigationControl } from "react-map-gl/maplibre";
import { TopographNavbar } from "../components/TopographNavbar";
import { BBoxSelector } from "../components/BBoxSelector";
import { MapSearchBar } from "../components/MapSearchBar";
import { CLIENT } from "../lib/api";
import type { Map as MapType } from "../lib/types";
import {
    bboxesFromLLUR,
    MAX_PANEL_DEPTH,
    MAX_PANEL_SIZE_X,
    MIN_PANEL_DEPTH,
    MIN_PANEL_SIZE_X,
    MIN_PANEL_SIZE_Y,
} from "../lib/map/bbox";
import {
    convertFromMM,
    convertToMm,
    estMeters,
    fmtDist,
    fmtLat,
    fmtLon,
    fmtNum,
    getParts,
    type LngLatLike,
} from "../lib/units";
import { estimateFramePieces, getPrice } from "../lib/pricing";
import { useAuth } from "../context/AuthContext";

const dollarFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const INITIAL_LL: LngLatLike = { lng: -122.5318, lat: 37.7048 };
const INITIAL_UR: LngLatLike = { lng: -122.3547, lat: 37.8169 };

function readQueryString() {
    const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
    const out: Partial<{
        ll: LngLatLike;
        ur: LngLatLike;
        width: number;
        title: string;
        depth: number;
        buildingScale: number;
    }> = {};
    if (params.w !== undefined && params.s !== undefined && params.e !== undefined && params.n !== undefined) {
        out.ll = { lng: parseFloat(params.w), lat: parseFloat(params.s) };
        out.ur = { lng: parseFloat(params.e), lat: parseFloat(params.n) };
    }
    if (params.width !== undefined) out.width = parseFloat(params.width);
    if (params.title !== undefined) out.title = params.title + " (copy)";
    if (params.max_z !== undefined) out.depth = parseFloat(params.max_z);
    if (params.building_scale !== undefined) out.buildingScale = parseFloat(params.building_scale);
    return out;
}

function getScale(ll: LngLatLike, ur: LngLatLike, width: number): number {
    if (!width) return 0;
    return (estMeters(ll, ur)[0] * 1000) / width;
}

function getScaleColor(scale: number): string {
    if (scale > 30000) return "#cc2929";
    if (scale > 20000) return "#c29d25";
    return "#25c26e";
}

function buildMap(
    ll: LngLatLike,
    ur: LngLatLike,
    width: number,
    depth: number,
    buildingScale: number,
    friendlyName: string,
): MapType {
    return {
        id: "",
        friendlyName,
        extent: { ll, ur },
        width: width || 0,
        maxX: MAX_PANEL_SIZE_X,
        maxZ: depth || 0,
        buildingScale,
        files: {},
        status: "unknown",
        createTime: 0,
    };
}

function getScaleHelp(scale: number): string {
    if (scale > 30000) {
        return "Details on this map are very tiny and unlikely to print well. Strongly suggest increasing the dimensions of the physical map or reducing your selection.";
    }
    if (scale > 20000) {
        return "Details on this map are small. Consider increasing the dimensions of the physical map or reducing your selection.";
    }
    return "";
}

export function Create() {
    const auth = useAuth();
    const mapRef = useRef<MapRef | null>(null);

    const [ll, setLL] = useState<LngLatLike>(INITIAL_LL);
    const [ur, setUR] = useState<LngLatLike>(INITIAL_UR);
    const [width, setWidth] = useState(300);
    const [height, setHeight] = useState(300);
    const [depth, setDepth] = useState(10);
    const [buildingScale, setBuildingScale] = useState(1);
    const [humanWidth, setHumanWidth] = useState("3 ft");
    const [humanHeight, setHumanHeight] = useState("3 ft");
    const [humanDepth, setHumanDepth] = useState("10 mm");
    const [widthInvalid, setWidthInvalid] = useState("");
    const [heightInvalid, setHeightInvalid] = useState("");
    const [depthInvalid, setDepthInvalid] = useState("");
    const [mapName, setMapName] = useState("");
    const [subtitle, setSubtitle] = useState<string | null>(null);
    const [toasts, setToasts] = useState<string[]>([]);

    const recenterMap = useCallback((a: LngLatLike, b: LngLatLike) => {
        if (!mapRef.current) return;
        mapRef.current.fitBounds(
            [[a.lng, a.lat], [b.lng, b.lat]],
            { padding: 40, duration: 500 },
        );
    }, []);

    // One-time initialization from query string.
    const didInit = useRef(false);
    useEffect(() => {
        if (didInit.current) return;
        didInit.current = true;

        const q = readQueryString();
        let nextLL = INITIAL_LL;
        let nextUR = INITIAL_UR;
        let nextWidth = 500;
        let nextHeight = 500;
        let nextDepth = depth;
        let nextBuildingScale = buildingScale;
        let title = "San Francisco";

        if (q.ll && q.ur) {
            nextLL = q.ll;
            nextUR = q.ur;
        }
        if (q.width !== undefined) {
            nextWidth = q.width;
            const [realW, realH] = estMeters(nextLL, nextUR);
            nextHeight = (nextWidth * realH) / realW;
        } else {
            const [realW, realH] = estMeters(nextLL, nextUR);
            nextHeight = (nextWidth * realH) / realW;
        }
        if (q.title !== undefined) title = q.title;
        if (q.depth !== undefined) nextDepth = q.depth;
        if (q.buildingScale !== undefined) nextBuildingScale = q.buildingScale;

        setLL(nextLL);
        setUR(nextUR);
        setWidth(nextWidth);
        setHeight(nextHeight);
        setDepth(nextDepth);
        setBuildingScale(nextBuildingScale);
        setMapName(title);
        setSubtitle(title);
        setHumanDepth(convertFromMM(getParts(humanDepth)[1] ?? "mm", nextDepth));
        // Wait for map ref to be ready, then fit bounds.
        setTimeout(() => recenterMap(nextLL, nextUR), 50);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep humanWidth/humanHeight in sync when width/height change.
    useEffect(() => {
        const unit = getParts(humanWidth)[1] ?? "mm";
        setHumanWidth(convertFromMM(unit, width));
    }, [width]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const unit = getParts(humanHeight)[1] ?? "mm";
        setHumanHeight(convertFromMM(unit, height));
    }, [height]); // eslint-disable-line react-hooks/exhaustive-deps

    // Validate whenever a dimension changes.
    useEffect(() => {
        setWidthInvalid(width < MIN_PANEL_SIZE_X ? `${humanWidth} < ${MIN_PANEL_SIZE_X} mm` : "");
    }, [width, humanWidth]);
    useEffect(() => {
        setHeightInvalid(height < MIN_PANEL_SIZE_Y ? `${humanHeight} < ${MIN_PANEL_SIZE_Y} mm` : "");
    }, [height, humanHeight]);
    useEffect(() => {
        let inv = "";
        if (depth < MIN_PANEL_DEPTH) inv = `${humanDepth} < ${MIN_PANEL_DEPTH} mm`;
        else if (depth > MAX_PANEL_DEPTH) inv = `${humanDepth} > ${MAX_PANEL_DEPTH} mm`;
        setDepthInvalid(inv);
    }, [depth, humanDepth]);

    const processWidth = () => {
        const newW = convertToMm(humanWidth);
        const unit = getParts(humanWidth)[1];
        if (!newW || !unit) {
            setWidthInvalid(`Don't understand "${humanWidth}" (try "6 in", "0.5 ft", "152 mm", etc.)`);
            return;
        }
        setWidth(newW);
        const [realW, realH] = estMeters(ll, ur);
        const newH = (newW * realH) / realW;
        setHeight(newH);
    };

    const processHeight = () => {
        const newH = convertToMm(humanHeight);
        const unit = getParts(humanHeight)[1];
        if (!newH || !unit) {
            setHeightInvalid(`Don't understand "${humanHeight}" (try "6 in", "0.5 ft", "152 mm", etc.)`);
            return;
        }
        setHeight(newH);
        const [realW, realH] = estMeters(ll, ur);
        const newW = (newH * realW) / realH;
        setWidth(newW);
    };

    const processDepth = () => {
        const newD = convertToMm(humanDepth);
        if (!newD) {
            setDepthInvalid(`Unknown "${humanDepth}" (try "6 in", "0.5 ft", "152 mm", etc.)`);
            return;
        }
        setDepth(newD);
    };

    const handleBoundsChange = (newLL: LngLatLike, newUR: LngLatLike) => {
        setLL(newLL);
        setUR(newUR);
        // Preserve width; update height to match aspect ratio.
        const [realW, realH] = estMeters(newLL, newUR);
        if (realW !== 0) {
            setHeight(Math.abs((width * realH) / realW));
        }
    };

    const onSearchSelect = (newLL: LngLatLike, newUR: LngLatLike, title: string) => {
        setLL(newLL);
        setUR(newUR);
        setWidth(500);
        setHeight(500);
        setSubtitle(title);
        setMapName(title);
        recenterMap(newLL, newUR);
    };

    const render = () => {
        // The Render button is already disabled while logged out, and the
        // OverlayTrigger on it explains why — so this is a belt-and-braces
        // guard, not a place to raise a dialog (DESIGN.md C-MSG-3).
        if (!auth.loggedIn) return;
        CLIENT.createMap({
            extent: { ll, ur },
            width: width || 0,
            maxX: MAX_PANEL_SIZE_X,
            maxZ: depth || 0,
            buildingScale,
            friendlyName: mapName,
        })
            .then((m) => setToasts((prev) => [...prev, m.id]))
            .catch((e) => console.error(e));
    };

    const scale = useMemo(() => getScale(ll, ur, width), [ll, ur, width]);
    const scaleColor = getScaleColor(scale);
    const scaleHelp = getScaleHelp(scale);

    const bboxCount = useMemo(
        () => bboxesFromLLUR(ll, ur, width, height).features.length,
        [ll, ur, width, height],
    );

    const framePieces = useMemo(
        () =>
            estimateFramePieces(
                buildMap(ll, ur, width, depth, buildingScale, mapName),
            ),
        [ll, ur, width, depth, buildingScale, mapName],
    );

    const estimatedCost = useMemo(
        () =>
            getPrice(
                buildMap(ll, ur, width, depth, buildingScale, mapName),
            ),
        [ll, ur, width, depth, buildingScale, mapName],
    );

    const dims = estMeters(ll, ur);

    return (
        <>
            <TopographNavbar subtitle={subtitle} />
            <div style={{ height: "100vh", width: "100vw" }}>
                <MapLibreMap
                    ref={mapRef}
                    initialViewState={{ longitude: 0, latitude: 0, zoom: 2 }}
                    mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
                    onLoad={() => recenterMap(ll, ur)}
                    style={{ width: "100%", height: "100%" }}
                >
                    <NavigationControl />
                    <BBoxSelector
                        ll={ll}
                        ur={ur}
                        width={width}
                        height={height}
                        onBoundsChange={handleBoundsChange}
                    />
                </MapLibreMap>
            </div>

            <div style={{ position: "absolute", zIndex: 1, top: "7em", left: "5em" }}>
                <MapSearchBar onSelect={onSearchSelect} />
            </div>

            <ToastContainer position="bottom-start" style={{ zIndex: 2, margin: "3em" }}>
                {toasts.map((id) => (
                    <Toast
                        key={id}
                        bg="success"
                        autohide
                        delay={15000}
                        onClose={() => setToasts((prev) => prev.filter((t) => t !== id))}
                    >
                        <Toast.Header>
                            <i className="bi bi-emoji-sunglasses me-2" />
                            <strong className="me-auto">Map {id.substring(0, 7)} Created</strong>
                        </Toast.Header>
                        <Toast.Body>
                            Go to the <a href="/renders">Renders tab</a> to view map {id.substring(0, 7)}{" "}
                            once it's created. This'll probably take a few minutes.
                        </Toast.Body>
                    </Toast>
                ))}
            </ToastContainer>

            <div style={{ position: "absolute", zIndex: 1, bottom: "3em", left: "3em" }}>
                <Card style={{ width: "40em" }}>
                    <Card.Header>
                        <Card.Title style={{ paddingTop: 10 }}>
                            <i className="bi bi-map" /> New Map
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        Click and drag the corners on the map to adjust the selected bounds. You can use
                        the tools below to control the geometry of the final product.
                        <br />
                        When you're done, click "Render."
                        <br /><br />
                        <table>
                            <tbody>
                                <tr>
                                    <td><i className="bi bi-fonts" /> Map Name</td>
                                    <td colSpan={3}>
                                        <Form.Control
                                            value={mapName}
                                            onChange={(e) => setMapName(e.currentTarget.value)}
                                            isInvalid={mapName === ""}
                                            placeholder="Map Title"
                                            style={{
                                                background: "transparent",
                                                color: "white",
                                                borderColor: "rgb(255, 191, 0)",
                                                width: "17.7rem",
                                                borderRadius: 5,
                                            }}
                                        />
                                        {mapName === "" && (
                                            <div style={{ color: "#ff6b6b", fontSize: "0.8em" }}>
                                                Your map must have a title!
                                            </div>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td><i className="bi bi-arrows-angle-expand" /> Dimensions</td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={humanWidth}
                                            onChange={(e) => setHumanWidth(e.currentTarget.value)}
                                            onBlur={processWidth}
                                            isInvalid={widthInvalid !== ""}
                                            style={{
                                                width: "7.5rem",
                                                background: "transparent",
                                                color: "white",
                                                borderColor: "#FFBF00",
                                            }}
                                        />
                                        {widthInvalid && (
                                            <div style={{ color: "#ff6b6b", fontSize: "0.8em" }}>{widthInvalid}</div>
                                        )}
                                    </td>
                                    <td>&nbsp;by&nbsp;</td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={humanHeight}
                                            onChange={(e) => setHumanHeight(e.currentTarget.value)}
                                            onBlur={processHeight}
                                            isInvalid={heightInvalid !== ""}
                                            style={{
                                                width: "7.5rem",
                                                background: "transparent",
                                                color: "white",
                                                borderColor: "#FFBF00",
                                            }}
                                        />
                                        {heightInvalid && (
                                            <div style={{ color: "#ff6b6b", fontSize: "0.8em" }}>{heightInvalid}</div>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Popover>
                                                    <Popover.Header>Help: Terrain Height</Popover.Header>
                                                    <Popover.Body>
                                                        The height of the terrain, not counting the extra space on the
                                                        base for snap-fits or any buildings. Buildings will be scaled
                                                        relative to this. If your spot is flat, 1cm is reasonable.
                                                        Otherwise you may want to increase it, but not beyond{" "}
                                                        {MAX_PANEL_DEPTH} mm.
                                                    </Popover.Body>
                                                </Popover>
                                            }
                                        >
                                            <span><i className="bi bi-arrow-bar-up" /> Terr Height</span>
                                        </OverlayTrigger>
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={humanDepth}
                                            onChange={(e) => setHumanDepth(e.currentTarget.value)}
                                            onBlur={processDepth}
                                            isInvalid={depthInvalid !== ""}
                                            style={{
                                                width: "7.5rem",
                                                background: "transparent",
                                                color: "white",
                                                borderColor: "#FFBF00",
                                            }}
                                        />
                                        {depthInvalid && (
                                            <div style={{ color: "#ff6b6b", fontSize: "0.8em" }}>{depthInvalid}</div>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Popover>
                                                    <Popover.Header>Help: Building Scale</Popover.Header>
                                                    <Popover.Body>
                                                        The relative scale of the building. By default, your map is
                                                        exactly 1:1 scale in all dimensions - buildings appear the
                                                        correct height and size against the terrain. You can set this
                                                        number to change the Z-scale and make your buildings taller
                                                        (scale {">"} 1) or shorter (scale {"<"} 1) than they "should" be.
                                                    </Popover.Body>
                                                </Popover>
                                            }
                                        >
                                            <span><i className="bi bi-building-up" /> Bldg Scale</span>
                                        </OverlayTrigger>
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            step={0.01}
                                            value={buildingScale}
                                            onChange={(e) => setBuildingScale(parseFloat(e.currentTarget.value))}
                                            style={{
                                                width: "7.5rem",
                                                background: "transparent",
                                                color: "white",
                                                borderColor: "#FFBF00",
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr><td colSpan={5}><hr /></td></tr>
                                <tr>
                                    <td><i className="bi bi-arrows-expand-vertical" /> Longitudes</td>
                                    <td style={{ textAlign: "right" }}>{fmtLon(Math.min(ll.lng, ur.lng))}</td>
                                    <td>&nbsp;to&nbsp;</td>
                                    <td>{fmtLon(Math.max(ll.lng, ur.lng))}</td>
                                    <td>&nbsp;({fmtDist(dims[0])})</td>
                                </tr>
                                <tr>
                                    <td><i className="bi bi-arrows-expand" /> Latitudes</td>
                                    <td style={{ textAlign: "right" }}>{fmtLat(Math.min(ll.lat, ur.lat))}</td>
                                    <td>&nbsp;to&nbsp;</td>
                                    <td>{fmtLat(Math.max(ll.lat, ur.lat))}</td>
                                    <td>&nbsp;({fmtDist(dims[1])})</td>
                                </tr>
                                <tr>
                                    <td><i className="bi bi-arrows-angle-expand" /> Scale</td>
                                    <td style={{ color: scaleColor }}>1:{fmtNum(scale, 1)}</td>
                                </tr>
                                <tr><td colSpan={5}><hr /></td></tr>
                            </tbody>
                        </table>
                        <div>
                            This map will be split into {bboxCount} tiles and {framePieces} frame pieces.
                            The estimated cost to print this is {dollarFmt.format(estimatedCost)}.
                        </div>
                        <div style={{ color: scaleColor }}>{scaleHelp}</div>
                    </Card.Body>
                    <Card.Footer>
                        <div style={{ textAlign: "right" }}>
                            <OverlayTrigger
                                placement="right"
                                overlay={
                                    !auth.loggedIn ? (
                                        <Popover>
                                            <Popover.Header>Authentication Error</Popover.Header>
                                            <Popover.Body>
                                                You are not logged in. Use the buttons in the upper right to log in.
                                            </Popover.Body>
                                        </Popover>
                                    ) : scaleHelp ? (
                                        <Popover>
                                            <Popover.Header>Map Error</Popover.Header>
                                            <Popover.Body>
                                                Something is preventing this map from being created. Check the notes to
                                                the left.
                                            </Popover.Body>
                                        </Popover>
                                    ) : (
                                        <span />
                                    )
                                }
                            >
                                <span>
                                    <Button
                                        variant="outline-warning"
                                        disabled={!auth.loggedIn || scaleHelp !== ""}
                                        onClick={render}
                                    >
                                        <i className="bi bi-play" /> Render
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        </div>
                    </Card.Footer>
                </Card>
            </div>
        </>
    );
}
